import { useState } from "react";
import DOMPurify from "dompurify";
import { Upload, ChevronRight, ChevronDown } from "lucide-react";
import { useAuth } from "../../../../contexts/AuthContext";
import { EditIconButton, DeleteIconButton, IconButton, ImageGallery } from "../../../../components/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ProjectFileKindEnum } from "../../../../types/projectFile";
import DiaryEntryFileItem, { DiaryEntryFileItemData } from "./DiaryEntryFileItem";

export interface TagItem {
  id: string;
  name: string;
  slug: string;
}

export interface DiaryEntryProjectFile extends DiaryEntryFileItemData {
  fileType?: ProjectFileKindEnum | null;
  contentType?: string | null;
}

export interface FileCountByMediaType {
  mediaType: string;
  count: number;
}

export interface ProjectFileMetadata {
  countsByMediaType: FileCountByMediaType[];
}

export interface DiaryEntryItem {
  id: string;
  date: string;
  description: string;
  tag?: TagItem | null;
  status: string;
  weather?: string | null;
  author: { id: string; fullName: string };
  projectFiles?: DiaryEntryProjectFile[] | null;
  projectFileMetadata?: ProjectFileMetadata | null;
}

interface DiaryEntryCardProps {
  entry: DiaryEntryItem;
  /** Display as "Log #n" when set; otherwise "Log" */
  logNumber?: number;
  /** When true, show Edit and Delete actions (admin and author only) */
  canEdit?: boolean;
  /** Project ID for file visibility toggle (log view). When set, visibility button is shown for users who can manage. */
  projectId?: string;
  /** Called after file visibility is toggled so the log list can refetch. */
  onFileVisibilityChange?: () => void;
  onEdit?: (entry: DiaryEntryItem) => void;
  onDelete?: (entry: DiaryEntryItem) => void;
  onUpload?: (entry: DiaryEntryItem) => void;
}

/** Map media type to a Badge variant + optional className override for non-standard colors. */
function getMediaTypeBadgeProps(mediaType: string): { variant: "destructive" | "info" | "warning" | "success" | "default"; className?: string } {
  const t = mediaType.toLowerCase();
  if (t === "pdf") return { variant: "destructive", className: "normal-case font-normal px-1.5 py-0.5" };
  if (t === "image") return { variant: "info", className: "normal-case font-normal px-1.5 py-0.5 bg-sky-100 text-sky-900 dark:bg-sky-950/60 dark:text-sky-200" };
  if (t === "document") return { variant: "warning", className: "normal-case font-normal px-1.5 py-0.5 bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200" };
  if (t === "spreadsheet") return { variant: "success", className: "normal-case font-normal px-1.5 py-0.5 bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200" };
  if (t === "text") return { variant: "default", className: "normal-case font-normal px-1.5 py-0.5 bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200" };
  return { variant: "default", className: "normal-case font-normal px-1.5 py-0.5" };
}

function getStatusLabel(status: string): string {
  const s = (status ?? "").toLowerCase();
  const labels: Record<string, string> = {
    normal: "Normal",
    delayed: "Atrasado",
    ahead_of_schedule: "Adiantado",
    issue: "Problema",
    milestone: "Marco",
  };
  return labels[s] ?? status ?? "—";
}

function getStatusVariant(status: string): "success" | "warning" | "teal" | "destructive" | "info" | "default" {
  const s = (status ?? "").toLowerCase();
  const variants: Record<string, "success" | "warning" | "teal" | "destructive" | "info" | "default"> = {
    normal: "success",
    delayed: "warning",
    ahead_of_schedule: "teal",
    issue: "destructive",
    milestone: "info",
  };
  return variants[s] ?? "default";
}

export default function DiaryEntryCard({
  entry,
  logNumber,
  canEdit = false,
  projectId,
  onFileVisibilityChange,
  onEdit,
  onDelete,
  onUpload,
}: DiaryEntryCardProps) {
  const [filesSectionOpen, setFilesSectionOpen] = useState(false);
  const { currentUser } = useAuth();
  const canManage =
    (currentUser?.role === "ADMIN" || currentUser?.role === "COLLABORATOR") && !!projectId;

  const photoFiles = (entry.projectFiles ?? []).filter(
    (pf) => pf.fileType === ProjectFileKindEnum.Photo && pf.url
  );
  const imageFiles = (entry.projectFiles ?? []).filter(
    (pf) =>
      pf.url &&
      (pf.mediaType?.toLowerCase() === "image" || pf.fileType === ProjectFileKindEnum.Photo)
  );
  const [galleryOpenIndex, setGalleryOpenIndex] = useState<number | null>(null);
  const openGallery = (index: number) => setGalleryOpenIndex(index);

  const heading = logNumber != null ? `Log #${logNumber}` : "Log";

  return (
    <Card>
      <CardContent className="pt-8">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <h3 className="text-xl font-light tracking-tight text-primary-950 dark:text-white mb-1">
              {heading}
            </h3>
            <p className="text-sm text-primary-600 dark:text-primary-400">
              {new Date(entry.date).toLocaleDateString("pt-BR")} • {entry.author.fullName}
            </p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
            <Badge variant={getStatusVariant(entry.status)}>
              {getStatusLabel(entry.status)}
            </Badge>
            {canEdit && (
              <div className="flex items-center gap-2">
                {onUpload && (
                  <IconButton
                    variant="secondary"
                    aria-label="Enviar documentos"
                    iconSize="sm"
                    onClick={() => onUpload(entry)}
                  >
                    <Upload strokeWidth={1.5} />
                  </IconButton>
                )}
                <EditIconButton
                  onClick={() => onEdit?.(entry)}
                  aria-label="Editar"
                  iconSize="sm"
                />
                <DeleteIconButton
                  onClick={() => onDelete?.(entry)}
                  aria-label="Excluir"
                  iconSize="sm"
                />
              </div>
            )}
          </div>
        </div>

        <div
          className="text-primary-900 dark:text-white mb-4 wrap-break-word font-light [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_a]:text-primary-800 dark:[&_a]:text-white [&_a]:underline [&_a:hover]:text-primary-600 dark:[&_a:hover]:text-primary-400"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(entry.description ?? "", {
              ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "a", "ul", "ol", "li", "h1", "h2", "h3"],
              ALLOWED_ATTR: ["href", "target", "rel"],
            }),
          }}
        />

        {entry.tag && (
          <div className="text-sm text-primary-600 dark:text-primary-400 mb-4">
            Etiqueta: {entry.tag.name}
          </div>
        )}

        {entry.weather && (
          <div className="text-sm text-primary-600 dark:text-primary-400 mb-4">
            Clima: {entry.weather}
          </div>
        )}

        {photoFiles.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {photoFiles.map((pf, idx) => (
              <img
                key={pf.id ?? idx}
                src={pf.url!}
                alt=""
                className="w-full h-48 object-cover border border-primary-200 dark:border-primary-800"
              />
            ))}
          </div>
        )}

        {entry.projectFiles && entry.projectFiles.length > 0 && (
          <div className="mt-4 pt-4 border-t border-primary-200 dark:border-primary-800">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setFilesSectionOpen((open) => !open)}
              className="w-full justify-start"
              aria-expanded={filesSectionOpen}
            >
              <span className="shrink-0 w-4 flex items-center justify-center text-current">
                {filesSectionOpen ? (
                  <ChevronDown className="w-3 h-3" strokeWidth={2} />
                ) : (
                  <ChevronRight className="w-3 h-3" strokeWidth={2} />
                )}
              </span>
              <span className="flex items-center gap-2 flex-wrap">
                ARQUIVOS
                {entry.projectFileMetadata?.countsByMediaType?.map((c) => {
                  const badgeProps = getMediaTypeBadgeProps(c.mediaType);
                  return (
                    <Badge
                      key={c.mediaType}
                      variant={badgeProps.variant}
                      className={badgeProps.className}
                    >
                      {c.mediaType}({c.count})
                    </Badge>
                  );
                })}
              </span>
            </Button>
            {imageFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {imageFiles.map((pf, idx) => (
                  <button
                    key={pf.id}
                    type="button"
                    onClick={() => openGallery(idx)}
                    className="shrink-0 w-16 h-16 overflow-hidden border border-primary-200 dark:border-primary-800 hover:border-primary-400 dark:hover:border-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-inset transition-colors duration-300 cursor-pointer"
                    aria-label={`Ver imagem ${idx + 1} de ${imageFiles.length}: ${pf.name}`}
                  >
                    <img
                      src={pf.url!}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
            {filesSectionOpen && projectId && (
              <ul className="space-y-2 mt-2">
                {entry.projectFiles.map((pf) => (
                  <DiaryEntryFileItem
                    key={pf.id}
                    file={pf}
                    projectId={projectId}
                    canManage={canManage}
                    onChange={onFileVisibilityChange}
                  />
                ))}
              </ul>
            )}
            {filesSectionOpen && !projectId && (
              <ul className="space-y-2 mt-2">
                {entry.projectFiles.map((pf) => (
                  <li key={pf.id} className="text-sm text-primary-600 dark:text-primary-400">
                    {pf.name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <ImageGallery
          files={imageFiles.map((f) => ({ id: f.id, url: f.url!, name: f.name }))}
          openIndex={galleryOpenIndex}
          onClose={() => setGalleryOpenIndex(null)}
        />
      </CardContent>
    </Card>
  );
}
